import { createApi } from "@reduxjs/toolkit/query/react";

import { getQueryString } from "@tsp/api/utils/propsToQuery";

import { fromBackendDate } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { CurrencyId } from "@tsp-domains/money/enums";
import { PartyUuid } from "@tsp-domains/parties/types";
import { SystemTypeId } from "@tsp-domains/system-types/enums";

import { AccountVerificationStatus } from "../enums";
import {
  AccountUuid,
  ArchiveUserAccountPayload,
  DefaultUserAccountsMap,
  GetAccountLinkUrlPayload,
  GetAccountLinkUrlResult,
  RequestAccountManualVerificationPayload,
  RestoreUserAccountPayload,
  SaveDefaultUserAccountPayload,
  UserAccount,
  UserAccounts,
} from "../types";

export type GetUserAccountsResponseData = {
  nextPayoutTs: number | null;
  userAccounts: Array<{
    uuid: AccountUuid;
    balance?: number;
    currency: {
      id: CurrencyId;
      name?: string;
    };
    details?: {
      number?: string;
      accountNumber?: string;
      maskedNumber?: string;
      serviceIds?: Array<number>;
    };
    externalId: string;
    externalName?: string;
    status: AccountVerificationStatus;
    systemType: SystemTypeId;
    createdAt: number;
    cardBinInfo?: {
      cardType?: string | null;
    } | null;
  }>;
  defaultAccounts: Record<PartyUuid, Partial<Record<CurrencyId, AccountUuid>>>;
};

export type GetAccountLinkUrkRequestParams = {
  contractorAccount: string;
};

export type GetAccountLinkUrkResponseData = {
  link: string;
};

export const accountsApi = createApi({
  reducerPath: "@accounts/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["UserAccountsInfo"],
  endpoints: (builder) => ({
    getUserAccountsInfo: builder.query<
      {
        userAccounts: UserAccounts;
        defaultUserAccountsMap: DefaultUserAccountsMap;
      },
      void,
      GetUserAccountsResponseData
    >({
      query: () => {
        return {
          url: "/getUserAccounts",
          method: "GET",
        };
      },
      transformResponse: (response: GetUserAccountsResponseData) => {
        return {
          userAccounts: response.userAccounts.map((userAccount): UserAccount => {
            return {
              uuid: userAccount.uuid,
              createdAt: fromBackendDate(userAccount.createdAt),
              externalId: userAccount.externalId,
              currencyId: userAccount.currency.id,
              systemTypeId: userAccount.systemType,
              verificationStatus: userAccount.status,
              cardBinInfo: userAccount.cardBinInfo,
              details: userAccount.details,
            };
          }),
          defaultUserAccountsMap: response.defaultAccounts,
        };
      },
      providesTags: ["UserAccountsInfo"],
    }),
    getAccountLinkUrl: builder.query<GetAccountLinkUrlResult, GetAccountLinkUrlPayload, GetAccountLinkUrkResponseData>({
      query: (payload) => {
        const body: GetAccountLinkUrkRequestParams = {
          contractorAccount: payload.contractorAccountUuid,
        };

        return {
          url: `/get-link-account-url?${getQueryString(body)}`,
          method: "GET",
        };
      },
      transformResponse: (response: GetAccountLinkUrkResponseData): GetAccountLinkUrlResult => {
        return response.link;
      },
    }),
    saveDefaultUserAccount: builder.mutation<void, SaveDefaultUserAccountPayload>({
      query: (payload) => {
        return {
          url: "/save-default-accounts",
          method: "POST",
          body: {
            accounts: {
              [payload.contractorUuid]: {
                [payload.currencyId]: payload.accountUuid,
              },
            },
          },
        };
      },
      invalidatesTags: ["UserAccountsInfo"],
    }),
    requestAccountManualVerification: builder.mutation<{ uuid: string }, RequestAccountManualVerificationPayload>({
      query: (payload) => {
        return {
          url: "/requestAccountManualVerification",
          method: "POST",
          body: {
            accountUuid: payload.accountUuid,
          },
        };
      },
      invalidatesTags: ["UserAccountsInfo"],
    }),
    archiveUserAccount: builder.mutation<{ accountUuid: string }, ArchiveUserAccountPayload>({
      query: (payload) => {
        return {
          url: "/archiveAccount",
          method: "POST",
          body: {
            uuid: payload.accountUuid,
          },
        };
      },
      invalidatesTags: ["UserAccountsInfo"],
    }),
    restoreUserAccount: builder.mutation<{ accountUuid: string }, RestoreUserAccountPayload>({
      query: (payload) => {
        return {
          url: "/restoreAccount",
          method: "POST",
          body: {
            uuid: payload.accountUuid,
          },
        };
      },
      invalidatesTags: ["UserAccountsInfo"],
    }),
  }),
});
